import { FiCopy, FiPlus, FiTrash2 } from "react-icons/fi";
import {
  ANNUAL_TURNOVER_RANGES,
  LEGAL_TYPES,
  MAX_BENEFICIAL_OWNERS,
  emptyPerson,
} from "../../constants/kyc";
import {
  ActivityPicker,
  CountryList,
  Field,
  OptionPills,
  PersonFields,
  SectionTitle,
  TextField,
  YesNo,
  kycStyles,
} from "./KycFields";

/**
 * A company's verification (KYB) — Keepz's form for a legal entity, in three parts.
 * The create-business wizard shows one part per step; the business's verification
 * window shows them all.
 *
 * Every section takes the whole form and reports changes as (path, value), e.g.
 * ("director.email", "…"), so the parent can clear that field's error.
 */

export function KybCompanySection({ form, onChange, errors = {}, disabled, isDark, t }) {
  const text = (path, props = {}) => (
    <TextField
      id={`kyb-${path}`}
      value={form[path]}
      onChange={(v) => onChange(path, v)}
      error={errors[path]}
      isDark={isDark}
      disabled={disabled}
      placeholder={t(`kyc.placeholders.${path}`)}
      {...props}
    />
  );

  return (
    <div className="space-y-5">
      <section>
        <SectionTitle title={t("kyc.sections.general")} isDark={isDark} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t("kyc.fields.companyName")} required error={errors.companyName} isDark={isDark} t={t} htmlFor="kyb-companyName">
            {text("companyName", { maxLength: 200, autoComplete: "organization" })}
          </Field>
          <Field label={t("kyc.fields.brandName")} required error={errors.brandName} isDark={isDark} t={t} htmlFor="kyb-brandName">
            {text("brandName", { maxLength: 200 })}
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("kyc.fields.legalType")} required error={errors.legalType} isDark={isDark} t={t}>
              <OptionPills
                label={t("kyc.fields.legalType")}
                options={LEGAL_TYPES.map((id) => ({ id, label: t(`kyc.legalTypes.${id}`) }))}
                value={form.legalType}
                onChange={(v) => onChange("legalType", v)}
                error={errors.legalType}
                disabled={disabled}
                isDark={isDark}
              />
            </Field>
          </div>
          {form.legalType === "other" && (
            <div className="sm:col-span-2">
              <Field label={t("kyc.fields.legalTypeOther")} required error={errors.legalTypeOther} isDark={isDark} t={t} htmlFor="kyb-legalTypeOther">
                {text("legalTypeOther", { maxLength: 100 })}
              </Field>
            </div>
          )}
          <Field
            label={t("kyc.fields.identificationCode")}
            hint={t("kyc.fields.identificationCodeHint")}
            required
            error={errors.identificationCode}
            isDark={isDark}
            t={t}
            htmlFor="kyb-identificationCode"
          >
            {text("identificationCode", {
              inputMode: "numeric",
              maxLength: 11,
              onChange: (v) => onChange("identificationCode", v.replace(/\D/g, "")),
            })}
          </Field>
          <Field label={t("kyc.fields.website")} optional error={errors.website} isDark={isDark} t={t} htmlFor="kyb-website">
            {text("website", { inputMode: "url", maxLength: 300, autoComplete: "url" })}
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("kyc.fields.iban")} required error={errors.iban} isDark={isDark} t={t} htmlFor="kyb-iban">
              {text("iban", {
                maxLength: 34,
                autoComplete: "off",
                onChange: (v) => onChange("iban", v.toUpperCase()),
              })}
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label={t("kyc.fields.acceptsForeignCards")} required error={errors.acceptsForeignCards} isDark={isDark} t={t}>
              <div className="max-w-xs">
                <YesNo
                  label={t("kyc.fields.acceptsForeignCards")}
                  value={form.acceptsForeignCards}
                  onChange={(v) => onChange("acceptsForeignCards", v)}
                  error={errors.acceptsForeignCards}
                  disabled={disabled}
                  isDark={isDark}
                  t={t}
                />
              </div>
            </Field>
          </div>
        </div>
      </section>

      <section>
        <SectionTitle title={t("kyc.sections.turnover")} isDark={isDark} />
        <OptionPills
          label={t("kyc.fields.annualTurnover")}
          options={ANNUAL_TURNOVER_RANGES}
          value={form.annualTurnover}
          onChange={(v) => onChange("annualTurnover", v)}
          error={errors.annualTurnover}
          disabled={disabled}
          isDark={isDark}
        />
        {errors.annualTurnover && <p className="mt-1.5 text-footnote text-status-danger">{errors.annualTurnover}</p>}
      </section>
    </div>
  );
}

export function KybActivitySection({ form, onChange, errors = {}, disabled, isDark, t }) {
  return (
    <section>
      <SectionTitle title={t("kyc.sections.activity")} isDark={isDark} />
      <ActivityPicker
        value={form.businessActivities}
        other={form.businessActivityOther}
        onChange={(v) => onChange("businessActivities", v)}
        onOtherChange={(v) => onChange("businessActivityOther", v)}
        error={errors.businessActivities}
        otherError={errors.businessActivityOther}
        disabled={disabled}
        isDark={isDark}
        t={t}
      />
    </section>
  );
}

export function KybPeopleSection({ form, onChange, errors = {}, disabled, isDark, t }) {
  const s = kycStyles(isDark);
  const owners = form.beneficialOwners || [];

  const copyDirector = (index) => {
    const { fullName, personalId, citizenship, pep, email, phone } = form.director;
    onChange(`beneficialOwners.${index}`, {
      ...owners[index],
      fullName,
      personalId,
      citizenship,
      pep,
      email,
      phone,
    });
  };

  return (
    <div className="space-y-6">
      <CountryList />
      <section>
        <SectionTitle title={t("kyc.sections.director")} isDark={isDark} />
        <PersonFields
          prefix="director"
          person={form.director}
          onChange={onChange}
          errors={errors}
          disabled={disabled}
          isDark={isDark}
          t={t}
        />
      </section>

      <section>
        <SectionTitle title={t("kyc.sections.owners")} hint={t("kyc.sections.ownersHint")} isDark={isDark} />
        {errors.beneficialOwners && (
          <p className="mb-3 text-footnote text-status-danger" role="alert">
            {errors.beneficialOwners}
          </p>
        )}
        <div className="space-y-3">
          {owners.map((owner, index) => (
            <div key={index} className={`rounded-radius-md border p-3 sm:p-4 ${s.card}`}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className={`text-footnote-semibold uppercase tracking-wide ${s.muted}`}>
                  {t("kyc.owner", { n: index + 1 })}
                </span>
                {!disabled && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => copyDirector(index)}
                      className={`inline-flex items-center gap-1.5 rounded-radius-xs px-2 py-1 text-footnote-semibold transition ${
                        "text-label-secondary hover:bg-fill-quaternary"
                      }`}
                    >
                      <FiCopy className="size-3.5" />
                      {t("kyc.sameAsDirector")}
                    </button>
                    {owners.length > 1 && (
                      <button
                        type="button"
                        onClick={() => onChange("beneficialOwners", owners.filter((_, i) => i !== index))}
                        className="inline-flex items-center gap-1.5 rounded-radius-xs px-2 py-1 text-footnote-semibold text-status-danger transition hover:bg-status-danger-bg"
                      >
                        <FiTrash2 className="size-3.5" />
                        {t("kyc.removeOwner")}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <PersonFields
                prefix={`beneficialOwners.${index}`}
                person={owner}
                onChange={onChange}
                errors={errors}
                disabled={disabled}
                isDark={isDark}
                t={t}
                withShare
              />
            </div>
          ))}
        </div>
        {!disabled && owners.length < MAX_BENEFICIAL_OWNERS && (
          <button
            type="button"
            onClick={() => onChange("beneficialOwners", [...owners, emptyPerson(true)])}
            className="mt-3 inline-flex items-center gap-1.5 text-footnote-semibold text-accent-text hover:underline"
          >
            <FiPlus className="size-3.5" />
            {t("kyc.addOwner")}
          </button>
        )}
      </section>

      <section>
        <label
          className={`flex cursor-pointer items-start gap-3 rounded-radius-md border p-3 sm:p-4 ${
            errors.confirmed ? "border-status-danger" : s.card
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <input
            type="checkbox"
            checked={form.confirmed === true}
            disabled={disabled}
            onChange={(e) => onChange("confirmed", e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-mint"
          />
          <span className="min-w-0">
            <span className={`block text-subheadline text-label-primary`}>
              {t("kyc.confirm")}
            </span>
            <span className={`mt-1 block ${s.hint}`}>{t("kyc.confirmHint")}</span>
          </span>
        </label>
        {errors.confirmed && <p className="mt-1.5 text-footnote text-status-danger">{errors.confirmed}</p>}
      </section>
    </div>
  );
}

/** The whole form at once — for a business verifying, or correcting, after it was created. */
export default function KybForm(props) {
  return (
    <div className="space-y-8">
      <KybCompanySection {...props} />
      <KybActivitySection {...props} />
      <KybPeopleSection {...props} />
    </div>
  );
}
